import './styles.css';

import {
  type SelectedCitation,
  useAgentChat,
} from '@/components/agent-chat/context/AgentChatContext';
import {
  AgentChatMessage,
  TAgentToolMessage,
} from '@/services/types/agent-chat-types';

import { AttachmentBadge } from './AttachmentBadge';
import {
  LOR_UPLOAD_REVIEW_TAG,
  LorReviewRow,
  normalizeLorReviewRow,
  parseLorReviewTable,
  STATUS_STYLE_MAP,
} from './lorUtils';
import { MessageContentViewer } from './MessageContentViewer';
import { CitationPill, Citations } from './tiptap-extensions/Citations';
import { ToolCallBadge } from './ToolCallBadge';

interface ToolCall {
  id?: string;
  name: string;
  args?: Record<string, unknown>;
  type?: 'tool_call';
}

const LorReviewTable = ({
  rows,
  matterId,
  onSelectCitation,
}: {
  rows: LorReviewRow[];
  matterId: string;
  onSelectCitation?: (citation: SelectedCitation | null) => void;
}) => {
  if (!rows.length) return null;

  return (
    <div className="mr-auto my-4 max-w-5xl overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-4 py-3 text-left">Field</th>
            <th className="px-4 py-3 text-left">Provided</th>
            <th className="px-4 py-3 text-left">Authoritative</th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Notes</th>
            <th className="px-4 py-3 text-left">Citations</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {rows.map((rawRow, index) => {
            const row = normalizeLorReviewRow(rawRow, index);
            if (!row) return null;

            const {
              field,
              provided,
              authoritative,
              status,
              notes,
              citations,
              id,
            } = row;

            const statusClass =
              STATUS_STYLE_MAP[status] ?? STATUS_STYLE_MAP.DEFAULT;

            return (
              <tr key={id}>
                <td className="px-4 py-3 font-medium text-slate-900">
                  {field}
                </td>
                <td className="px-4 py-3 text-slate-700">{provided}</td>
                <td className="px-4 py-3 text-slate-700">{authoritative}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusClass}`}
                  >
                    {status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-700">{notes}</td>
                <td className="px-4 py-3 text-slate-700">
                  {citations.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {citations.map((citation, citeIndex) => {
                        if (citation.kind === 'modern' && onSelectCitation) {
                          return (
                            <CitationPill
                              key={`${id}-citation-${citation.id}-${citeIndex}`}
                              citationId={citation.id}
                              matterId={matterId}
                              setSelectedCitation={onSelectCitation}
                            />
                          );
                        }
                        // Legacy citations are stripped but not rendered as badges
                        if (citation.kind === 'legacy') {
                          return null;
                        }
                        if (citation.kind === 'text') {
                          return (
                            <span
                              key={`${id}-text-${citation.label}-${citeIndex}`}
                              className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 dark:bg-cyan-200 dark:text-cyan-900 dark:border-cyan-300 px-2 py-0.5 text-[11px] font-mono text-slate-700"
                            >
                              {citation.label}
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

interface MessageProps {
  message: AgentChatMessage;
  toolMessages: Record<string, TAgentToolMessage>;
  isAwaitingResponse?: boolean;
  onRemoveAttachment?: (attachmentId: string) => Promise<void>;
  matterId: string;
  threadId: string;
}

export const Message = ({
  message,
  toolMessages,
  isAwaitingResponse = false,
  onRemoveAttachment,
  matterId,
  threadId,
}: MessageProps) => {
  const { setSelectedCitation } = useAgentChat();
  // Check if this is a tool call message
  const hasToolCalls =
    message.message.type === 'ai' &&
    message.message.tool_calls &&
    message.message.tool_calls.length > 0;

  // Get tool call information if applicable
  const toolCalls = (
    hasToolCalls ? message.message.tool_calls : []
  ) as ToolCall[];

  // Parse content based on its type
  const { content } = message.message;
  const isUploadReviewPrompt =
    typeof content === 'string' && content.includes(LOR_UPLOAD_REVIEW_TAG);
  const lorReview =
    message.message.type === 'ai'
      ? parseLorReviewTable(content as string)
      : null;
  const displayContent =
    !isUploadReviewPrompt &&
    lorReview?.remainingContent &&
    typeof lorReview.remainingContent === 'string'
      ? lorReview.remainingContent
      : !isUploadReviewPrompt
        ? content
        : '';

  // Check if there's actual content to display
  const hasContent =
    typeof displayContent === 'string' && displayContent.trim() !== '';

  // Check for attachments
  const hasAttachments = message.attachments && message.attachments.length > 0;

  const getToolMessage = (toolCallId: string | undefined) => {
    if (!toolCallId) return null;
    return toolMessages[toolCallId];
  };

  return (
    <div>
      {hasContent && (
        <div
          className={`rounded-lg w-fit max-w-3xl ${
            message.message.type === 'human'
              ? 'bg-accent ml-auto px-3 py-2'
              : 'bg-transparent mr-auto'
          }`}
        >
          <div>
            <MessageContentViewer
              content={displayContent as string}
              isStreaming={message.message.type === 'AIMessageChunk'}
              extensions={[
                Citations.configure({
                  matterId,
                  threadId,
                }),
              ]}
            />
          </div>
        </div>
      )}

      {lorReview && lorReview.rows.length > 0 && (
        <LorReviewTable
          rows={lorReview.rows}
          matterId={matterId}
          onSelectCitation={setSelectedCitation}
        />
      )}

      {hasAttachments && (
        <div
          className={`flex justify-end flex-wrap gap-2 mt-2 ${
            message.message.type === 'human' ? 'ml-auto' : 'mr-auto'
          } max-w-3xl`}
        >
          {message.attachments.map((attachment) => (
            <AttachmentBadge
              key={attachment.id}
              id={attachment.id}
              name={attachment.name}
              size={attachment.size}
              url={attachment.attachment}
              contentType={attachment.content_type}
              onRemove={onRemoveAttachment || (async () => {})}
              isAttached
            />
          ))}
        </div>
      )}

      {hasToolCalls && (
        <div className="flex flex-col gap-1 w-full max-w-2xl mt-1">
          {toolCalls.map((toolCall, index) => (
            <ToolCallBadge
              matterId={matterId}
              threadId={threadId}
              key={toolCall.id || index}
              toolName={toolCall.name}
              args={toolCall.args}
              isLoading={isAwaitingResponse}
              isDone={!isAwaitingResponse}
              isError={getToolMessage(toolCall.id)?.status === 'error'}
              toolMessage={getToolMessage(toolCall.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
